;(() => {
  const namespace = (globalThis.StylistLocator = globalThis.StylistLocator || {})

  // ============================================================================
  // Distance Calculations
  // ============================================================================

  const MILES_TO_METERS = 1609.34
  const EARTH_RADIUS_METERS = 6371000
  const EARTH_RADIUS_EQUATORIAL_METERS = 6378137

  const ROLE_CATEGORY_BY_ROLE = {
    'Elite Artist': 'elite',
    'Master Technician': 'masters',
    'Certified Stylist': 'certified_stylist',
    'Certified Salon': 'certified_salon',
  }
  const VALID_ROLE_NAMES = new Set(Object.keys(ROLE_CATEGORY_BY_ROLE))

  const toRadians = (value) => (value * Math.PI) / 180
  const getRoleName = (stylist) => (stylist?.role || '').trim()

  /**
   * Check if a stylist has a valid stylist role (Elite Artist, Master Technician, or Certified Stylist).
   * @param {Object} stylist - Stylist object
   * @returns {boolean} True if has valid stylist role
   */
  function hasValidStylistRole(stylist) {
    return VALID_ROLE_NAMES.has(getRoleName(stylist))
  }

  /**
   * Compute distance between two coordinates in meters using Haversine formula.
   * @param {number} lat1 - First latitude
   * @param {number} lng1 - First longitude
   * @param {number} lat2 - Second latitude
   * @param {number} lng2 - Second longitude
   * @returns {number} Distance in meters
   */
  function computeDistanceMeters(lat1, lng1, lat2, lng2) {
    const dLat = toRadians(lat2 - lat1)
    const dLng = toRadians(lng2 - lng1)
    const a =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(toRadians(lat1)) * Math.cos(toRadians(lat2)) * Math.sin(dLng / 2) * Math.sin(dLng / 2)
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a))
    return EARTH_RADIUS_METERS * c
  }

  /**
   * Compute bounding box from a center point and radius.
   * @param {Object} center - { lat, lng }
   * @param {number} radiusMiles - Radius in miles
   * @returns {Object} Bounds { latitude_low, latitude_high, longitude_low, longitude_high }
   */
  function computeBoundsFromCenter(center, radiusMiles) {
    const radiusMeters = radiusMiles * MILES_TO_METERS
    const latDelta = (radiusMeters / EARTH_RADIUS_EQUATORIAL_METERS) * (180 / Math.PI)
    const constrainedLat = Math.max(-89.999, Math.min(89.999, center.lat))
    const cosLat = Math.cos((constrainedLat * Math.PI) / 180)
    const lngDelta = cosLat ? (radiusMeters / (EARTH_RADIUS_EQUATORIAL_METERS * cosLat)) * (180 / Math.PI) : 180

    return {
      latitude_low: Math.max(-90, center.lat - latDelta),
      latitude_high: Math.min(90, center.lat + latDelta),
      longitude_low: Math.max(-180, center.lng - lngDelta),
      longitude_high: Math.min(180, center.lng + lngDelta),
    }
  }

  // ============================================================================
  // Zoom Calculations
  // ============================================================================

  /**
   * Calculate appropriate zoom level to fit a radius circle on the map.
   * @param {number} radiusMiles - Radius in miles
   * @param {google.maps.Map} map - Map instance
   * @param {HTMLElement} mapContainer - Map container element
   * @param {Object} center - Center coordinates { lat, lng }
   * @returns {number} Zoom level (6-15)
   */
  function calculateZoomForRadius(radiusMiles, map, mapContainer, center) {
    // Fallback if map or center not available
    if (!center || (!map && !mapContainer)) {
      if (radiusMiles <= 5) return 13
      if (radiusMiles <= 10) return 12
      if (radiusMiles <= 25) return 11
      if (radiusMiles <= 50) return 10
      if (radiusMiles <= 100) return 9
      return 8
    }

    const radiusMeters = radiusMiles * MILES_TO_METERS
    const diameterMeters = radiusMeters * 2
    const container = mapContainer || (map && map.getDiv())
    const mapWidth = container ? container.offsetWidth : 800
    const mapHeight = container ? container.offsetHeight : 600
    const mapSize = Math.min(mapWidth, mapHeight)
    const targetSizeMeters = diameterMeters / 0.8

    const latRad = (center.lat * Math.PI) / 180
    const cosLat = Math.cos(latRad)
    const zoom = Math.log2((mapSize * 156543.03392 * cosLat) / targetSizeMeters)

    return Math.max(3, Math.min(15, Math.round(zoom)))
  }

  // ============================================================================
  // Filtering
  // ============================================================================

  /**
   * Filter stylists by radius from a center point.
   * @param {Array} stylists - Array of stylists
   * @param {number} radiusMiles - Radius in miles
   * @param {Object} center - Center point { lat, lng }
   * @returns {Array} Filtered stylists within radius
   */
  function filterStylistsByRadius(stylists, radiusMiles, center) {
    if (!radiusMiles || Number.isNaN(radiusMiles) || !center) {
      return stylists
    }
    const limitMeters = radiusMiles * MILES_TO_METERS
    return stylists.filter((stylist) => {
      if (!Number.isFinite(stylist.latitude) || !Number.isFinite(stylist.longitude)) {
        return false
      }
      return computeDistanceMeters(center.lat, center.lng, stylist.latitude, stylist.longitude) <= limitMeters
    })
  }

  /**
   * Filter stylists to only show those with valid roles or salon owners.
   * @param {Array} stylists - Array of stylists
   * @returns {Array} Filtered stylists with valid roles
   */
  function filterStylistsByValidRoles(stylists) {
    return stylists.filter((stylist) => {
      // Always show salon owners regardless of role
      if (stylist.isSalonOwner === true) {
        return true
      }
      return VALID_ROLE_NAMES.has(getRoleName(stylist))
    })
  }

  /**
   * Filter stylists for locator visibility.
   * Always include salon owners so they can appear as Certified Salon pins/cards,
   * even when they are not elevate members.
   * @param {Array} stylists - Array of stylists
   * @returns {Array} Filtered stylists
   */
  function filterStylistsByElevateMember(stylists) {
    return stylists.filter((stylist) => stylist.elevateMember === true || stylist.isSalonOwner === true)
  }

  /**
   * Filter stylists by certification type.
   * @param {Array} stylists - Array of stylists
   * @param {string} certification - Certification filter value
   * @returns {Array} Filtered stylists
   */
  function filterStylistsByCertification(stylists, certification) {
    if (!certification || certification === 'all') {
      return stylists
    }
    return stylists.filter((stylist) => matchesCertificationFilter(stylist, certification))
  }

  /**
   * Check if a stylist matches a certification filter.
   * For salon owners: show when certified_salon filter is selected.
   * @param {Object} stylist - Stylist object
   * @param {string} certification - Certification filter value
   * @returns {boolean} True if stylist matches filter
   */
  function matchesCertificationFilter(stylist, certification) {
    // Salon owners show for certified_salon filter
    if (stylist.isSalonOwner === true) {
      return certification === 'certified_salon' || certification === 'all'
    }

    // Regular stylist logic
    const roleCategory = ROLE_CATEGORY_BY_ROLE[getRoleName(stylist)]

    switch (certification) {
      case 'certified_salon':
        return false
      case 'elite':
        return roleCategory === 'elite'
      case 'masters':
        return roleCategory === 'masters'
      case 'certified_stylist':
        return roleCategory === 'certified_stylist'
      default:
        return true
    }
  }

  // ============================================================================
  // Role Category
  // ============================================================================

  /**
   * Determine the role category for a stylist (used for marker icons).
   * @param {Object} stylist - Stylist object
   * @returns {string} Role category: 'certified_salon', 'elite', 'masters', 'certified_stylist', or 'default'
   */
  function determineRoleCategory(stylist) {
    if (!stylist) return 'default'

    // Salon owners get salon category (star pin)
    if (stylist.isSalonOwner === true) {
      return 'certified_salon'
    }

    return ROLE_CATEGORY_BY_ROLE[getRoleName(stylist)] || 'default'
  }

  // ============================================================================
  // Exports
  // ============================================================================

  namespace.geometry = Object.assign({}, namespace.geometry, {
    computeDistanceMeters,
    computeBoundsFromCenter,
    calculateZoomForRadius,
    filterStylistsByRadius,
    filterStylistsByValidRoles,
    filterStylistsByElevateMember,
    filterStylistsByCertification,
    matchesCertificationFilter,
    determineRoleCategory,
    hasValidStylistRole,
  })
})()
